/**
 * Projectile - Base class for bullets, arrows, lasers, etc.
 */

import { Vector3 } from '../math/Vector3';
import { Vector2 } from '../math/Vector2';
import { Renderer } from '../core/Renderer';

export type ProjectileType = 'bullet' | 'shell' | 'laser' | 'plasma' | 'rail';

export interface ProjectileConfig {
  type: ProjectileType;
  damage: number;
  speed: number;
  range: number;
  color: string;
  size: number;
  piercing?: boolean;      // Can hit multiple enemies
  explosive?: boolean;     // Deals AoE damage
  explosionRadius?: number;
  trailLength?: number;    // Visual trail effect
}

export class Projectile {
  public position: Vector3;
  public velocity: Vector3;
  public config: ProjectileConfig;
  public isActive: boolean = true;
  public distanceTraveled: number = 0;
  public ownerId: string;  // To prevent self-damage

  // Visual trail positions
  private trailPositions: Vector3[] = [];
  private maxTrailLength: number = 5;

  // Pending destroy - allows projectile to render one more frame after hitting
  private pendingDestroy: boolean = false;

  constructor(
    startPosition: Vector3,
    direction: Vector2,
    config: ProjectileConfig,
    ownerId: string
  ) {
    this.position = startPosition.clone();
    this.config = config;
    this.ownerId = ownerId;

    // Normalize direction and apply speed
    const normalizedDir = direction.magnitude() > 0 ? direction.normalize() : new Vector2(1, 0);
    this.velocity = new Vector3(
      normalizedDir.x * config.speed,
      normalizedDir.y * config.speed,
      0
    );

    this.maxTrailLength = config.trailLength ?? 5;
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    // If pending destroy, now fully deactivate (after render had a chance to show it)
    if (this.pendingDestroy) {
      this.isActive = false;
      return;
    }

    // Store trail position
    if (this.config.type === 'laser' || this.config.type === 'plasma' || this.config.type === 'rail') {
      this.trailPositions.unshift(this.position.clone());
      if (this.trailPositions.length > this.maxTrailLength) {
        this.trailPositions.pop();
      }
    }

    // Move projectile
    const movement = new Vector3(
      this.velocity.x * deltaTime,
      this.velocity.y * deltaTime,
      0
    );

    this.position.x += movement.x;
    this.position.y += movement.y;

    // Track distance
    this.distanceTraveled += Math.sqrt(movement.x * movement.x + movement.y * movement.y);

    // Deactivate if exceeded range
    if (this.distanceTraveled >= this.config.range) {
      this.isActive = false;
    }
  }

  render(renderer: Renderer): void {
    if (!this.isActive) return;

    const screenPos = renderer.worldToScreen(this.position);
    const ctx = renderer.getContext();
    const zoom = renderer.getZoom();

    ctx.save();

    switch (this.config.type) {
      case 'bullet':
        this.renderBullet(ctx, screenPos, zoom);
        break;
      case 'shell':
        this.renderShell(ctx, screenPos, zoom);
        break;
      case 'laser':
        this.renderLaser(ctx, screenPos, zoom, renderer);
        break;
      case 'plasma':
        this.renderPlasma(ctx, screenPos, zoom, renderer);
        break;
      case 'rail':
        this.renderRail(ctx, screenPos, zoom, renderer);
        break;
    }

    ctx.restore();
  }

  private renderBullet(ctx: CanvasRenderingContext2D, screenPos: Vector2, zoom: number): void {
    const size = this.config.size * zoom;

    // Simple bullet - small circle with motion blur
    ctx.fillStyle = this.config.color;
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, size, 0, Math.PI * 2);
    ctx.fill();

    // Motion trail
    ctx.fillStyle = this.config.color + '80';
    const trailDir = new Vector2(-this.velocity.x, -this.velocity.y).normalize();
    for (let i = 1; i <= 3; i++) {
      ctx.beginPath();
      ctx.arc(
        screenPos.x + trailDir.x * size * i * 1.5,
        screenPos.y + trailDir.y * size * i * 1.5,
        size * (1 - i * 0.2),
        0, Math.PI * 2
      );
      ctx.fill();
    }
  }

  private renderShell(ctx: CanvasRenderingContext2D, screenPos: Vector2, zoom: number): void {
    const size = this.config.size * zoom;

    // Shotgun shell - multiple pellets
    ctx.fillStyle = this.config.color;
    for (let i = 0; i < 5; i++) {
      const offsetX = (Math.random() - 0.5) * size * 2;
      const offsetY = (Math.random() - 0.5) * size * 2;
      ctx.beginPath();
      ctx.arc(screenPos.x + offsetX, screenPos.y + offsetY, size * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderLaser(ctx: CanvasRenderingContext2D, screenPos: Vector2, zoom: number, renderer: Renderer): void {
    const size = this.config.size * zoom;

    // Laser beam with glow
    ctx.strokeStyle = this.config.color;
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.shadowColor = this.config.color;
    ctx.shadowBlur = size * 2;

    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y);

    // Draw trail
    for (const trailPos of this.trailPositions) {
      const trailScreen = renderer.worldToScreen(trailPos);
      ctx.lineTo(trailScreen.x, trailScreen.y);
    }
    ctx.stroke();

    // Core glow
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, size * 0.5, 0, Math.PI * 2);
    ctx.fill();
  }

  private renderPlasma(ctx: CanvasRenderingContext2D, screenPos: Vector2, zoom: number, renderer: Renderer): void {
    const size = this.config.size * zoom;

    // Plasma ball with pulsing glow
    const pulse = 1 + Math.sin(Date.now() * 0.02) * 0.2;

    // Outer glow
    const gradient = ctx.createRadialGradient(
      screenPos.x, screenPos.y, 0,
      screenPos.x, screenPos.y, size * 2 * pulse
    );
    gradient.addColorStop(0, this.config.color);
    gradient.addColorStop(0.5, this.config.color + '80');
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, size * 2 * pulse, 0, Math.PI * 2);
    ctx.fill();

    // Core
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, size * 0.7, 0, Math.PI * 2);
    ctx.fill();

    // Trail
    ctx.strokeStyle = this.config.color + '60';
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y);
    for (const trailPos of this.trailPositions) {
      const trailScreen = renderer.worldToScreen(trailPos);
      ctx.lineTo(trailScreen.x, trailScreen.y);
    }
    ctx.stroke();
  }

  private renderRail(ctx: CanvasRenderingContext2D, screenPos: Vector2, zoom: number, renderer: Renderer): void {
    const size = this.config.size * zoom;

    // Rail gun - bright beam with electric effects
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = size * 2;
    ctx.lineCap = 'round';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = size * 4;

    ctx.beginPath();
    ctx.moveTo(screenPos.x, screenPos.y);

    for (const trailPos of this.trailPositions) {
      const trailScreen = renderer.worldToScreen(trailPos);
      // Add slight randomness for electric effect
      const jitterX = (Math.random() - 0.5) * size;
      const jitterY = (Math.random() - 0.5) * size;
      ctx.lineTo(trailScreen.x + jitterX, trailScreen.y + jitterY);
    }
    ctx.stroke();

    // Core
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(screenPos.x, screenPos.y, size, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Check if projectile hits a circular target */
  checkCollision(targetPos: Vector3, targetRadius: number): boolean {
    const dx = this.position.x - targetPos.x;
    const dy = this.position.y - targetPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < (this.config.size + targetRadius);
  }

  /** Mark projectile for destruction (will render one more frame before deactivating) */
  destroy(): void {
    this.pendingDestroy = true;
  }

  /** Check if projectile is pending destruction (hit something but still rendering) */
  isPendingDestroy(): boolean {
    return this.pendingDestroy;
  }
}

/** Manages all active projectiles */
export class ProjectileManager {
  private projectiles: Projectile[] = [];

  add(projectile: Projectile): void {
    this.projectiles.push(projectile);
  }

  update(deltaTime: number): void {
    // Update all projectiles
    for (const projectile of this.projectiles) {
      projectile.update(deltaTime);
    }

    // Remove inactive projectiles
    this.projectiles = this.projectiles.filter(p => p.isActive);
  }

  render(renderer: Renderer): void {
    for (const projectile of this.projectiles) {
      projectile.render(renderer);
    }
  }

  getActiveProjectiles(): Projectile[] {
    return this.projectiles.filter(p => p.isActive);
  }

  clear(): void {
    this.projectiles = [];
  }
}
